import { useState } from "react";
import { toast } from "sonner";
import { ShieldOff, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// Coach/admin waives a player's initial assessment (lifts all assessment booking gates).
export function WaiveAssessmentButton({ player, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/coach/players/${player.id}/assessment-override`, { action: "waive", reason: reason.trim() });
      toast.success(`Assessment waived — ${player.name} can now book any session`);
      const r = await api.get(`/students/${player.id}`);
      onDone?.(r.data);
      setOpen(false);
      setReason("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't waive"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="assessment-waive"
        className="px-4 py-2.5 text-xs inline-flex items-center gap-2 font-bold uppercase tracking-wider border border-[#525252] text-[#A3A3A3] hover:text-white hover:border-white transition-colors">
        <ShieldOff className="w-3.5 h-3.5" /> Waive assessment
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F0F0F] border border-[#262626] max-w-md" data-testid="assessment-waive-dialog">
          <DialogHeader>
            <DialogTitle>Waive {player.name}'s assessment?</DialogTitle>
            <DialogDescription className="text-sm text-[#A3A3A3] pt-1">
              All assessment booking restrictions are lifted straight away. This is logged in their activity history with your name and today's date.
            </DialogDescription>
          </DialogHeader>
          <label className="block mt-2">
            <span className="label-mono text-[#A3A3A3]">Reason (optional)</span>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={3}
              placeholder="e.g. Assessed at previous club, coach knows player well"
              data-testid="assessment-waive-reason"
              className="mt-1 w-full bg-[#141414] border border-[#262626] focus:border-[#CCFF00] focus:outline-none p-2 text-sm" />
          </label>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={() => setOpen(false)} data-testid="assessment-waive-cancel"
              className="px-4 py-2 text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white">Cancel</button>
            <button type="button" onClick={confirm} disabled={busy} data-testid="assessment-waive-confirm"
              className="min-h-[40px] px-4 py-2 text-xs uppercase tracking-wider font-bold bg-[#CCFF00] text-black inline-flex items-center gap-1.5 disabled:opacity-40">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Confirm waiver
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
